import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useSearchParams } from "react-router"
import axios from 'axios';

import { v4 as uuidv4 } from 'uuid';
import posthog from 'posthog-js';

import { AppContext } from './context.js';
import { sanitizeBody } from './utils/sanitizePHI.js';
import { registerEventServiceWorker, unregisterEventServiceWorker } from './serviceWorkerRegistration';

import './css/flexboxgrid.min.css';
//import './css/bootstrap-grid.min.css';
import './css/bootstrap-grid-utils.min.css';
import './css/app.css';
import './css/utils.css';
import './css/editor.css';

import './css/typo.scss';
import './css/navigation.scss';
import './css/avatar.scss';
import './css/card.scss';
import './css/button.scss';
import './css/input.scss';
import './css/status.scss';
import './css/counter.scss';
import './css/modal.scss';
import './css/datepicker.scss';
import './css/print.css';
import './css/tailwind.css';

///////////////////////////
/////     Desktop     /////
///////////////////////////

//////////////////////////////
/////     Components     /////
//////////////////////////////
import DesktopSpinner from "./desktop/components/spinner.js";
import DesktopError from "./desktop/components/error.js";
import DesktopSessionTimer from "./desktop/components/session-timer.js";
import DesktopNavigation from './desktop/components/navigation.js';

//////////////////////////
/////     Public     /////
//////////////////////////
import DesktopPublicLogin from './desktop/public/login.js';
import DesktopPublicForget from './desktop/public/forget.js';
import DesktopPublicReferral from './desktop/public/referral.js';
import DesktopPublicSignature from './desktop/public/signature.js';
import DesktopPublicPartner from './desktop/public/partner.js';

/////////////////////////////
/////     Physician     /////
/////////////////////////////

// Create
import DesktopPhysicianCreate from './desktop/physician/create.js';

// Dashboard
import DesktopPhysicianDashboard from './desktop/physician/dashboard.js';

// Intakes
import DesktopPhysicianIntakes from './desktop/physician/intakes.js';
import DesktopPhysicianIntake from './desktop/physician/intake.js';
//import DesktopPhysicianIntakeCreate from './desktop/physician/intake-create.js';

// Projects
import DesktopPhysicianExport from './desktop/physician/export.js';
import DesktopPhysicianExport2 from './desktop/physician/export2.js';

// Assessments
import DesktopPhysicianAssessment from './desktop/physician/assessment.js';

// Patients
import DesktopPhysicianPatients from './desktop/physician/patients.js';
import DesktopPhysicianPatient from './desktop/physician/patient.js';
import DesktopPhysicianPatientCreate from "./desktop/physician/patient-create.js";
import DesktopPhysicianCaregiverCreate from "./desktop/physician/caregiver-create.js";

// Referrals
import DesktopPhysicianReferrals from './desktop/physician/referrals.js';
import DesktopPhysicianReferral from './desktop/physician/referral.js';
import DesktopPhysicianReferralCreate from "./desktop/physician/referral-create.js";

// Accounting
import DesktopPhysicianAccounting from "./desktop/physician/accounting.js";

// Claims
import DesktopPhysicianClaims from './desktop/physician/claims.js';

// Analytics
import DesktopPhysicianAnalyticsUsersAssignmentOverview from "./desktop/physician/analytics/users/index.js";
import DesktopPhysicianAnalyticsPatientsStatistics from "./desktop/physician/analytics/patients/index.js";
import DesktopPhysicianAnalyticsPatientsSourceOverview from "./desktop/physician/analytics/patients/source-overview.js";
import DesktopPhysicianAnalyticsProjectsStatistics from "./desktop/physician/analytics/projects/index.js";
import DesktopPhysicianAnalyticsReferralsStatistics from "./desktop/physician/analytics/referrals/index.js";
import DesktopPhysicianAnalyticsAccountingStatistics from "./desktop/physician/analytics/accounting/index.js";
import DesktopPhysicianAnalyticsReportsPaaf from "./desktop/physician/analytics/reports/paaf.js";

// Settings
import DesktopPhysicianSettings from './desktop/physician/settings/index.js';
import DesktopPhysicianSettingsGeneral from './desktop/physician/settings/general.js';
import DesktopPhysicianSettingsRespiteBenefits from './desktop/physician/settings/respite-benefits.js';
import DesktopPhysicianSettingsClaimSettings from './desktop/physician/settings/claim-settings.js';
import DesktopPhysicianSettingsUsers from './desktop/physician/settings/users.js';
import DesktopPhysicianSettingsUser from './desktop/physician/settings/user.js';
import DesktopPhysicianSettingsUsersCreate from './desktop/physician/settings/user-create.js';

import DesktopPhysicianSettingsTemplatesContacts from './desktop/physician/settings/templates/contacts/index.js';
import DesktopPhysicianSettingsTemplatesContactsCreate from './desktop/physician/settings/templates/contacts/create.js';
import DesktopPhysicianSettingsTemplatesContactsEdit from './desktop/physician/settings/templates/contacts/edit.js';

import DesktopPhysicianSettingsPartners from './desktop/physician/settings/partners.js';
import DesktopPhysicianSettingsPartner from './desktop/physician/settings/partner.js';
import DesktopPhysicianSettingsPartnersCreate from './desktop/physician/settings/partner-create.js';

// Profile
import DesktopPhysicianProfileSettings from './desktop/physician/profile/settings.js';
import DesktopPhysicianFeedback from './desktop/physician/feedback.js';

///////////////////////////
/////     Partner     /////
///////////////////////////
import DesktopPartnerReferrals from './desktop/partner/referrals.js';
import DesktopPartnerReferral from './desktop/partner/referral.js';
import DesktopPartnerReferralCreate from './desktop/partner/referral-create.js';

// Redirect component for form to referral
function RedirectFormToReferral() {
  const { id } = useParams();
  return <Navigate to={`/referral/${id}`} replace />;
}

// Map leftover project-page tabs onto patient-card tabs. `tasks` (and anything
// unknown) is omitted so the patient page picks the current lifecycle step.
const PROJECT_TAB_TO_PATIENT = {
  services: "accounts",
  carePlan: "careplan",
  appointments: "appointments",
  assessments: "assessments",
  documents: "documents",
};

function RedirectProjectToPatient() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    axios.post('/api/admin/projects/get', { id })
      .then((response) => {
        const patientID = response.data?.project?.patientID;
        if (!patientID) {
          navigate('/patients', { replace: true });
          return;
        }
        const mappedTab = PROJECT_TAB_TO_PATIENT[searchParams.get('tab')];
        const to = mappedTab
          ? `/patient/${patientID}/card?tab=${mappedTab}`
          : `/patient/${patientID}/card`;
        navigate(to, { replace: true });
      })
      .catch(() => {
        navigate('/patients', { replace: true });
      });
  }, [id, navigate, searchParams]);

  return null;
}

function App() {
  const [loading, setLoading] = useState(true);

  const [token, setToken] = useState(null);

  const [role, setRole] = useState(null);
  const [staffRole, setStaffRole] = useState(null);
  const [id, setID] = useState(null);
  const [name, setName] = useState(null);
  const [lang, setLang] = useState(null);
  const [dateFormat, setDateFormat] = useState(null);
  const [timeFormat, setTimeFormat] = useState(null);
  const [expireAt, setExpireAt] = useState(null);
  const [org, setOrg] = useState(null);

  // PostHog-backed feature flags for this tenant, fetched once a session
  // exists. null while unloaded/logged out — gated UI should treat that as "off".
  const [features, setFeatures] = useState(null);

  const [spiners, setSpiners] = useState([]);

  const [errors, setErrors] = useState([]);

  const [scale, setScale] = useState(null);

  const [notificationObject, setNotificationObject] = useState({
    projects: 0,
    forms: 0,
    events: [],
  });

  // Browser tab init
  useEffect(() => {
    // All variables should be loaded from session storage
    let lsToken = localStorage.getItem('token');
    let lsRole = localStorage.getItem('role');
    let lsStaffRole = localStorage.getItem('staffRole');
    let lsID = localStorage.getItem('id');
    let lsName = localStorage.getItem('name');
    let lsLang = localStorage.getItem('lang');
    let lsDateFormat = localStorage.getItem('dateFormat');
    let lsTimeFormat = localStorage.getItem('timeFormat');
    let lsExpireAt = localStorage.getItem('expireAt');
    let lsOrg = localStorage.getItem('org');

    // Set initial scale and add resize listener
    rescale();
    window.addEventListener("resize", rescale);

    // Set data to session storage
    setToken(lsToken);
    setRole(lsRole);
    setStaffRole(lsStaffRole);
    setID(lsID);
    setName(lsName);
    setLang(lsLang);
    setDateFormat(lsDateFormat);
    setTimeFormat(lsTimeFormat);
    setExpireAt(lsExpireAt);
    setOrg(lsOrg);

    if (lsOrg) {
      posthog.group('organization', lsOrg);
    }

    // If user is logged in - but some data is missing - clear session
    if (lsToken !== null && (lsRole === null || lsID === null || lsName === null || lsLang === null || lsDateFormat === null || lsTimeFormat === null || lsExpireAt === null)) {
      clearState();
      // TODO: if token is exist - get missing data from server
    }

    // If token is available - set axios defaults
    if (lsToken !== null) {
      // Set global axios Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${lsToken}`;

      // Trigger event service worker
      manageEventListening(lsToken);
    }

    setLoading(false);
  }, []);

  // Load per-tenant PostHog feature flags whenever a session becomes
  // available (initial load with a stored token, login, logout).
  useEffect(() => {
    if (!token) {
      setFeatures(null);
      return;
    }
    axios.get('/api/admin/features').then((r) => {
      setFeatures(r.data);
    }).catch(() => {
      // Fail closed — gated UI stays hidden if flags can't be loaded.
      setFeatures({});
    });
  }, [token]);

  function manageEventListening(token) {
    //console.log("MANAGE EVENT LISTENING", token);

    // Register the event service worker if the token is available
    if (token) {
      registerEventServiceWorker(token);

      // Listen for messages from the service worker
      if (navigator.serviceWorker) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (!event.data) {
            return;
          }

          switch (event.data.type) {
            case 'NEW_EVENTS':
              console.log('Received new events:', event.data.events);
              setNotificationObject(event.data.events ? event.data.events : null);
              break;
            case 'UNREGISTER_SERVICE_WORKER':
              unregisterEventServiceWorker();

              // Remove the event listener for messages from the service worker
              if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', () => {});
              }
              
              break;
            default:
              break;
          }
        });
      }
    } else {
      unregisterEventServiceWorker();

      // Remove the event listener for messages from the service worker
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', () => {});
      }
    }
  }

  function rescale() {
    let scale;
    let height = window.innerHeight;
    let width = window.innerWidth;
    if (width >= 1400) {
      scale = 'xxl';
		} else if (width >= 1200) {
      scale = 'xl';
		} else if (width > 992) {
			scale = 'lg';
		} else if (width > 768) {
			scale = 'md';
		} else if (width > 576) {
			scale = 'sm';
		} else {
			scale = 'xs';
    }
    setScale(scale);
  }

  // Token manipulation in store
  function setState(newToken, newRole, newID, newName, newLang, newDateFormat, newTimeFormat, newExpireAt, newOrg, newStaffRole) {
    localStorage.setItem('token', newToken);
    localStorage.setItem('role', newRole);
    localStorage.setItem('staffRole', newStaffRole);
    localStorage.setItem('id', newID);
    localStorage.setItem('name', newName);
    localStorage.setItem('lang', newLang);
    localStorage.setItem('dateFormat', newDateFormat);
    localStorage.setItem('timeFormat', newTimeFormat);
    localStorage.setItem('expireAt', newExpireAt);
    localStorage.setItem('org', newOrg);

    setToken(newToken);
    setRole(newRole);
    setStaffRole(newStaffRole);
    setID(newID);
    setName(newName);
    setLang(newLang);
    setDateFormat(newDateFormat);
    setTimeFormat(newTimeFormat);
    setExpireAt(newExpireAt);
    setOrg(newOrg);

    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    if (newID && posthog.get_distinct_id() !== newID) {
      posthog.identify(newID, { role: newRole });
    }
    posthog.register({ user_role: newRole });
    if (newOrg) {
      posthog.group('organization', newOrg);
    }

    // Trigger event service worker
    manageEventListening(newToken);
  }

  function clearState() {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('staffRole');
    localStorage.removeItem('id');
    localStorage.removeItem('name');
    localStorage.removeItem('lang');
    localStorage.removeItem('dateFormat');
    localStorage.removeItem('timeFormat');
    localStorage.removeItem('expireAt');
    localStorage.removeItem('org');

    setToken(null);
    setRole(null);
    setStaffRole(null);
    setID(null);
    setName(null);
    setLang(null);
    setDateFormat(null);
    setTimeFormat(null);
    setExpireAt(null);
    setOrg(null);
    setFeatures(null);

    setSpiners([]);
    setErrors([]);

    posthog.reset();

    // Trigger event service worker
    manageEventListening(null);
  }

  function addSpiner() {
    var u = uuidv4();
    var object = {id: u};
    setSpiners(prevSpiners => [...prevSpiners, object]);
    return u;
  }

  function safeSanitize(value) {
    try {
      return sanitizeBody(value);
    } catch {
      return '';
    }
  }

  function buildErrorProps(e) {
    let requestKeys = [];
    try {
      const raw = e.config?.data;
      if (raw) {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (parsed && typeof parsed === 'object') {
          requestKeys = Object.keys(parsed);
        }
      }
    } catch {
      // ignore — keys are best-effort
    }
    const props = {
      url: e.config?.url,
      method: e.config?.method,
      route: window.location.pathname,
      error_name: e.name,
      error_message: e.message,
      user_role: role,
    };
    const reqBody = safeSanitize(e.config?.data);
    if (reqBody) props.request_body = reqBody;
    if (requestKeys.length) props.request_data_keys = requestKeys;
    return props;
  }

  function addError(e) {
    console.log(e);

    const baseProps = buildErrorProps(e);

    if (e.response === undefined){
      posthog.captureException(e, {
        ...baseProps,
        network: true,
      });
      return;
    }

    const respProps = {
      ...baseProps,
      status: e.response.status,
      code: e.response.data?.code,
    };
    const respBody = safeSanitize(e.response.data);
    if (respBody) respProps.response_body = respBody;
    posthog.captureException(e, respProps);

    switch (e.response.status) {
      case 401:
        console.log('Unauthorized');
        clearState();
        break;
      default:
        var u = uuidv4();
        var object = {id: u, message: e.response.data.error, code: e.response.data?.code};
        setErrors(prevErrors => [...prevErrors, object]);
        break;
    }
  }

  function removeSpiner(id) {
    setSpiners(prevSpiners => prevSpiners.filter((item) => item.id !== id));
  }

  function removeError(id) {
    setErrors(prevErrors => prevErrors.filter((item) => item.id !== id));
  }

  function addMessage(message) {
    var u = uuidv4();
    setErrors(prevErrors => [...prevErrors, { id: u, message }]);
  }
  
  if (loading) {
    return (
      <></>
    );
  }

  const isAdmin = role === "physician" && staffRole === "admin";

  return (
    <AppContext.Provider value={{
      // App state variables
      token, role, id, name, lang, dateFormat, timeFormat, expireAt, org,
      // Staff permission level (admin/navigator), separate from the portal role
      staffRole, isAdmin,
      // Feature flags (PostHog, per-tenant)
      features,
      // System variables
      scale,
      // App state functions
      setState, clearState,
      // Spinner functions
      spiners, addSpiner, removeSpiner,
      // Request error functions
      errors, addError, removeError, addMessage,
    }}>
      <BrowserRouter>
        {scale !== 'xs' ? 

          // Desktop version
          !token ? (

            // Public context
            <Routes>
              <Route path="/partner/:id" element={<DesktopPublicPartner />} />
              <Route path="/form/:id" element={<RedirectFormToReferral />} />
              <Route path="/referral/:id" element={<DesktopPublicReferral />} />
              <Route path="/signature" element={<DesktopPublicSignature />} />
              <Route path="/forget" element={<DesktopPublicForget />} />
              <Route path="/" element={<DesktopPublicLogin />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          ) : (role === "physician") ? (
            // Physician context
            <DesktopNavigation notificationObject={notificationObject}>
              <Routes>
                {/* Create */}
                <Route path="/create" element={<DesktopPhysicianCreate />} />

                {/* Intakes */}
                <Route path="/intakes" element={<DesktopPhysicianIntakes />} />
                {/*<Route path="/intake/:id" element={<DesktopPhysicianIntake />} />*/}

                {/* Projects */}
                <Route path="/projects" element={<Navigate to="/patients" replace />} />
                <Route path="/project/:id" element={<RedirectProjectToPatient />} />

                {/* Assessments */}
                <Route path="/assessment/:id" element={<DesktopPhysicianAssessment />} />

                {/* Patients */}
                <Route path="/patients" element={<DesktopPhysicianPatients notificationObject={notificationObject} />} />
                <Route path="/patient/:id" element={<DesktopPhysicianPatientCreate />} />
                <Route path="/caregiver/:id" element={<DesktopPhysicianCaregiverCreate />} />
                <Route path="/patient/:id/card" element={<DesktopPhysicianPatient />} />

                {/* Referrals */}
                <Route path="/referrals" element={<DesktopPhysicianReferrals notificationObject={notificationObject} />} />
                <Route path="/referral/:id" element={<DesktopPhysicianReferral />} />
                <Route path="/referral/create" element={<DesktopPhysicianReferralCreate />} />

                {/* Intakes */}
                <Route path="/intakes" element={<DesktopPhysicianIntakes notificationObject={notificationObject} />} />
                <Route path="/intake/:id" element={<DesktopPhysicianIntake />} />
                {/* <Route path="/intake/create" element={<DesktopPhysicianIntakeCreate />} /> */}

                {/* Export */}
                <Route path="/export" element={<DesktopPhysicianExport />} />
                <Route path="/export2/:id" element={<DesktopPhysicianExport2 />} />

                {/* Accounting */}
                <Route path="/accounting" element={<DesktopPhysicianAccounting />} />

                {/* Claims */}
                {features?.claims && <Route path="/claims" element={<DesktopPhysicianClaims />} />}

                {/* Analytics */}
                <Route path="/analytics/">
                  <Route path="users/assignment-overview" element={<DesktopPhysicianAnalyticsUsersAssignmentOverview />} />
                  <Route path="patients/statistics" element={<DesktopPhysicianAnalyticsPatientsStatistics />} />
                  <Route path="patients/source-overview" element={<DesktopPhysicianAnalyticsPatientsSourceOverview />} />
                  <Route path="projects/statistics" element={<DesktopPhysicianAnalyticsProjectsStatistics />} />
                  <Route path="referrals/statistics" element={<DesktopPhysicianAnalyticsReferralsStatistics />} />
                  <Route path="accounting/statistics" element={<DesktopPhysicianAnalyticsAccountingStatistics />} />
                  <Route path="reports/paaf" element={<DesktopPhysicianAnalyticsReportsPaaf />} />
                </Route>

                {/* Settings */}
                <Route path="/settings/">
                  <Route path="" element={<DesktopPhysicianSettings />} />
                  <Route path="general" element={<DesktopPhysicianSettingsGeneral />} />
                  <Route path="respite-benefits" element={<DesktopPhysicianSettingsRespiteBenefits />} />
                  <Route path="claim-settings" element={<DesktopPhysicianSettingsClaimSettings />} />
                  <Route path="claim-amounts" element={<Navigate to="/settings/claim-settings" replace />} />

                  <Route path="users/create" element={isAdmin ? <DesktopPhysicianSettingsUsersCreate /> : <Navigate to="/settings/users" replace />} />
                  <Route path="users" element={<DesktopPhysicianSettingsUsers />} />
                  <Route path="user/:id" element={<DesktopPhysicianSettingsUser />} />

                  <Route path="partners/create" element={<DesktopPhysicianSettingsPartnersCreate />} />
                  <Route path="partners" element={<DesktopPhysicianSettingsPartners />} />
                  <Route path="partner/:id" element={<DesktopPhysicianSettingsPartner />} />

                  <Route path="templates/contacts/edit/:id" element={<DesktopPhysicianSettingsTemplatesContactsEdit />} />
                  <Route path="templates/contacts/create" element={<DesktopPhysicianSettingsTemplatesContactsCreate />} />
                  <Route path="templates/contacts" element={<DesktopPhysicianSettingsTemplatesContacts />} />
                </Route>

                {/* Profile */}
                <Route path="/profile/">
                  <Route path="settings" element={<DesktopPhysicianProfileSettings />} />
                </Route>

                {/* Feedback */}
                <Route path="/feedback" element={<DesktopPhysicianFeedback />} />

                {/* Dashboard */}
                <Route path="/" element={<DesktopPhysicianDashboard />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>

              <DesktopSpinner spiners={spiners} />
              <DesktopError errors={errors} />
              <DesktopSessionTimer />
            </DesktopNavigation>
          ) : role === "patient" ? (
            // Patient context
            <>
              Patient context
            </>
          ) : (
            // Partner context
            <DesktopNavigation notificationObject={notificationObject}>
              <Routes>
                {/* Referrals */}
                <Route path="/referrals" element={<DesktopPartnerReferrals />} />
                <Route path="/referral/create" element={<DesktopPartnerReferralCreate />} />
                <Route path="/referral/:id" element={<DesktopPartnerReferral />} />

                {/* Dashboard */}
                <Route path="/" element={<Navigate to="/referrals" />} />
                <Route path="*" element={<Navigate to="/referrals" />} />
              </Routes>

              <DesktopSpinner spiners={spiners} />
              <DesktopError errors={errors} />
              <DesktopSessionTimer />
            </DesktopNavigation>
          )
        : 
          // Mobile version
          <>
          </>
        }
      </BrowserRouter>
    </AppContext.Provider>
  );
}

export default App;
